import { datadogLogs } from "@datadog/browser-logs";
import { LDClient, LDContext } from "launchdarkly-js-client-sdk";
import { useFlags, useLDClient } from "launchdarkly-react-client-sdk";
import mixpanel from "mixpanel-browser";
import { useCallback, useEffect, useMemo, useState } from "react";
import { pageview as gaPageview, initialize } from "react-ga";

import { AnyObject, App, User, Workspace } from "@examspark/types";

import { useAppContext } from "../providers/AppContext";

export const anonymousUserGUID = "26503692-2fd6-4cd5-b66b-953c42f72a3b";

const identifyMixpanel = (user: User, mixpanelId: string | undefined) => {
  if (!mixpanelId) {
    datadogLogs.logger.warn("Identify user: Mixpanel ID not set");
    return;
  }
  if (!mixpanel) {
    datadogLogs.logger.warn("Identify user: Mixpanel not initialized");
    return;
  }
  mixpanel.identify(user.id);
  mixpanel.people.set({
    $first_name: user.firstName,
    $last_name: user.lastName,
    $email: `${user.email}`,
  });
};

const identifyLaunchDarkly = (
  ldClient: LDClient | undefined,
  params: Partial<{
    app: Pick<App, "id">;
    user: Pick<User, "id" | "email" | "firstName" | "lastName">;
    workspace: Pick<Workspace, "id">;
  }>,
) => {
  const { app, user, workspace } = params;

  const ldContext: LDContext = {
    kind: "multi",
  };

  // Track user
  if (user) {
    const userEmailDomain = user.email?.split("@")[1];
    if (userEmailDomain?.match(/examspark.com/)) {
      ldContext.user = {
        kind: "user",
        key: user.id,
        email: user.email,
        name: `${user.firstName} ${user.lastName}`,
      };
    }
  }

  if (workspace) {
    ldContext.workspace = {
      kind: "workspace",
      key: workspace.id,
    };
  }

  if (app) {
    ldContext.app = {
      kind: "app",
      key: app.id,
    };
  }

  if (!ldClient) {
    // console.warn("Identifying User: LaunchDarkly: No client");
    datadogLogs.logger.warn("Identifying User: LaunchDarkly: No client");
    return;
  }
  if (!process.env.REACT_APP_LD_CLIENT_ID) {
    // console.error("Identifying User: LaunchDarkly: No client ID");
    datadogLogs.logger.error("Identifying User: LaunchDarkly: No client ID");
    return;
  }

  datadogLogs.logger.info("Identifying User: LaunchDarkly", {
    context: ldContext,
  });
  // console.log("Identifying User: LaunchDarkly", ldContext);

  ldClient.identify(ldContext, undefined, (err) => {
    if (err) {
      datadogLogs.logger.error(`Identifying User: LaunchDarkly: Error: ${err}`);
    }
  });
};

export const useAnalytics = () => {
  const mixpanelId = process.env.REACT_APP_MIXPANEL_ID;
  const [state] = useAppContext();
  const [stateWorkspaceId, setStateWorkspaceId] = useState<string>();
  const { mixpanelMode = "basic" } = useFlags<{
    mixpanelMode?: "basic" | "detailed" | "disabled";
  }>();

  useEffect(() => {
    if (stateWorkspaceId != null) return;
    setStateWorkspaceId(state.workspaceUser?.id);
  }, [state.workspaceUser?.id, stateWorkspaceId]);

  const ldClient = useLDClient();

  const isTesting = () => {
    return "Cypress" in window && !!window.Cypress;
  };

  const identify = useCallback(
    (
      user: User,
      params?: {
        appId?: string;
        workspaceId?: string;
      },
    ) => {
      const workspaceId = params?.workspaceId ?? state.workspaceUser?.id;
      const appId =
        params?.appId ??
        state.app?.id ??
        process.env.REACT_APP_EXAMSPARK_APP_ID;

      if (isTesting()) return;

      datadogLogs.logger.info(`Identifying user ${user.id}`);

      identifyMixpanel(user, mixpanelId);
      identifyLaunchDarkly(ldClient, {
        user: user,
        app: appId ? { id: appId } : undefined,
        workspace: workspaceId ? { id: workspaceId } : undefined,
      });
      setStateWorkspaceId(workspaceId);
    },
    [ldClient, mixpanelId, state.app, state.workspaceUser?.id],
  );

  const appId = state.app?.id;

  const trackEvent = useCallback(
    (event: string, data?: AnyObject, workspaceId?: string) => {
      if (isTesting()) return;
      if (!mixpanelId) return;
      if (mixpanelMode === "disabled") return;
      if (
        DetailedAnalyticsEventNames.includes(event as AnalyticsEventName) &&
        mixpanelMode !== "detailed"
      ) {
        return;
      }

      if (appId) {
        if (!data) data = {};
        data.appId = appId;
      }

      if (workspaceId ?? stateWorkspaceId) {
        mixpanel.track_with_groups(event, data || {}, {
          workspace: [workspaceId ?? stateWorkspaceId],
        });
        return;
      }

      mixpanel.track(event, data);
    },
    [mixpanelId, mixpanelMode, appId, stateWorkspaceId],
  );

  const pageview = useCallback((pathname: string) => {
    if (!process.env.REACT_APP_GA_ID) return;
    // if (!params?.trackPageLoads) return;
    gaPageview(pathname);
  }, []);

  return useMemo(
    () => ({
      identify,
      trackEvent,
      isTesting,
      pageview,
    }),
    [identify, pageview, trackEvent],
  );
};

/**
 * A controlled vocabulary of event names to send to our analytics tools. Names
 * marked legacy go against best-practices for event naming (swapping noun/verb)
 * and should be migrated sometime, but we need to update our analytics reports
 * to support it.
 *
 * https://pkoullick.medium.com/whats-sexy-naming-conventions-for-analytics-events-58685e53c8d9
 */

export enum AnalyticsEventName {
  AccountDelete = "Delete Account",
  AccountReset = "Reset Account",
  ActivityAssign = "Assign Activity",
  ActivityComplete = "Complete Activity",
  ActivityCreateLegacy = "Activity Create",
  ActivityReset = "Reset Activity",
  ActivityStartLegacy = "Activity Start",
  ApplicationLaunchLegacy = "App Launch",
  ClassroomCreate = "Create Classroom",
  ClassroomUnitFinish = "Finish Unit",
  ClassroomUnitReset = "Reset Unit",
  ClassroomUnitSkip = "Skip Unit",
  ClassroomUnitStart = "Start Unit",
  ContentTranslate = "Translate Content",
  LaunchProxyApp = "Launch Proxy App",
  Login = "Login",
  Logout = "Logout",
  PageView = "View Page",
  RceAction = "RCE: Action",
}

const DetailedAnalyticsEventNames: AnalyticsEventName[] = [
  AnalyticsEventName.PageView,
  AnalyticsEventName.RceAction,
];

export const initGoogleAnalytics = () => {
  if (!process.env.REACT_APP_GA_ID) return;

  initialize(process.env.REACT_APP_GA_ID, {
    gaOptions: {
      cookieFlags: "samesite=none;secure",
    },
  });
};
